import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { navigate } from '@reach/router';
import { Helmet } from 'react-helmet';
import { Button } from 'chaoskit/src/components';
import { styles } from 'chaoskit/src/helpers/styles';
import { InstantSearch, Configure } from 'react-instantsearch-dom';
import algoliaSearch from 'algoliasearch/lite';

import { Icon } from '../components';
import { config } from '../helpers/config';
import BaseLayout from '../layouts/BaseLayout';
import Hero from '../partials/Hero';
import useDebounce from '../hooks/useDebounce';

import {
  createUrl,
  searchStateToUrl,
  urlToSearchState,
} from '../partials/search/utility';
import {
  CustomClearRefinements,
  CustomInfiniteHits,
  CustomSearchBox,
} from '../partials/search/algolia-global';
import {
  CustomTopicsRefinementList,
  CustomTypesRefinementList,
  Hit,
} from '../partials/search/algolia-resources';

const searchClient = algoliaSearch(
  config.algolia.applicationId,
  config.algolia.apiKey
);

const Resources = ({ location }) => {
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [searchState, setSearchState] = useState(urlToSearchState(location));

  // Debounce search changes
  const debouncedSearchState = useDebounce(searchState, 150);

  useEffect(() => {
    navigate(
      searchStateToUrl(location.pathname, debouncedSearchState),
      debouncedSearchState,
      { replace: true }
    );
  }, [debouncedSearchState]);

  const handleSearchStateChange = updatedSearchState => {
    setSearchState(updatedSearchState);
  };

  // Make sure we close mobile nav if resizing from mobile to desktop
  const checkSize = event => {
    if (event.matches) {
      setFilterOpen(true);
    } else {
      setFilterOpen(false);
    }
  };

  useEffect(() => {
    // Always scroll to top on mount
    window.scrollTo(0, 0);

    const viewportMediumMin = window.matchMedia(
      `(min-width: ${styles.viewport.medium})`
    );

    // Check on-load
    checkSize(viewportMediumMin);

    // Add listener
    viewportMediumMin.addListener(checkSize);

    return () => {
      // Clean-up
      viewportMediumMin.removeListener(checkSize);
    };
  }, []);

  return (
    <BaseLayout>
      <Helmet title="Resources" />
      <InstantSearch
        indexName="resources"
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={handleSearchStateChange}
        createURL={createUrl}
      >
        <Configure facetingAfterDistinct />
        <Hero
          compact
          contrast={false}
          particles={false}
          className="u-gradient--darkBlue-purple hero--resources"
        >
          <div className="hero-content">
            <div className="container">
              <div className="hero-titleWrapper u-contrast">
                <h1 className="hero-title">Resources</h1>
              </div>
              <div className="row u-flexCenter">
                <div className="column-9@medium column-7@large">
                  <div className="search__input-wrapper">
                    <CustomSearchBox placeholderText="Search Resources" />
                  </div>
                </div>
              </div>
              <aside className="u-contrast u-mt--large">
                <div
                  className={cx('u-textCenter u-hiddenUp@medium', {
                    'u-mb--regular': isFilterOpen,
                  })}
                >
                  <Button
                    type="default"
                    size="small"
                    onClick={() => setFilterOpen(!isFilterOpen)}
                  >
                    Filter{' '}
                    <Icon
                      className="u-ml--xsmall"
                      icon={isFilterOpen ? 'caret-down' : 'caret-right'}
                      size="small"
                    />
                  </Button>
                </div>
                <div
                  className={cx({
                    'u-hidden': !isFilterOpen,
                  })}
                >
                  <CustomTypesRefinementList
                    attribute="articleLabel"
                    limit={12}
                  />
                  <hr />
                  <CustomTopicsRefinementList attribute="topics" limit={24} />
                  <div className="resources__clearRefinements">
                    <CustomClearRefinements />
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </Hero>
        <section className="section section--resources">
          <div className="container">
            <CustomInfiniteHits
              hitTemplate={Hit}
              gridClasses="blockGrid-2@small blockGrid-3@medium"
            />
          </div>
        </section>
      </InstantSearch>
    </BaseLayout>
  );
};

Resources.propTypes = {
  location: PropTypes.object.isRequired,
};

export default Resources;
